import {Box, Button, Carousel, PigmentThemeProvider, Typography} from '@customink/pigment-react';
import {useMobileBreakpoint} from '@customink/pigment-react/lib/utils/useBreakpoint';
import React, {useEffect, useState, useRef} from 'react';
import {QueryClientProvider} from 'react-query';
import useRecentSavedDesign from '../../hooks/queries/recentSaveDesign';
import queryClient from '../../src/shared/queryClient';
import {HOST} from '../../constants';
import useDesignTracking from './useDesignTracking';

const CAROUSEL_HEIGHT = '228px';
const MOBILE_CAROUSEL_HEIGHT = 'auto';
const SAVE_INTERACTION_LABEL = 'your latest design save display';
const CUSTOM_INTERACTION_LABEL = 'design custom t-shirts display';
const EDIT_INTERACTION_LABEL = 'edit my design click';
const VIEW_INTERACTION_LABEL = 'view my designs click';

const DesignDetails = ({name, product_name, updated_at, isMobile}) => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column', 
    gap: isMobile ? '0.5rem' : '0.75rem', 
    paddingTop: isMobile ? '0.5rem' : '1rem'
  }}>
    <Typography variant="captionLarge" sx={{fontSize: isMobile ? '12px' : '14px', color: '#615F5F'}}>{name}</Typography>
    <Typography variant="captionLarge" sx={{fontSize: isMobile ? '12px' : '14px', color: '#615F5F'}}>{product_name}</Typography>
    <Typography variant="captionLarge" sx={{fontSize: isMobile ? '12px' : '14px', color: '#615F5F'}}>
      Last Saved: {updated_at}
    </Typography>
  </Box>
);

const DesignActions = ({onEdit, onView, isMobile}) => (
  <Box sx={{ 
    display: 'flex', 
    gap: isMobile ? '0.5rem' : '0.5rem', 
    flexDirection: 'column', 
    paddingTop: isMobile ? '0.5rem' : '1rem'
  }}>
    <Button 
      onClick={onEdit}
      size={isMobile ? 'small' : 'medium'}
      sx={isMobile ? { fontSize: '12px', padding: '0.5rem 0.75rem' } : {}}
    >
      Edit Design
    </Button>
    <Button 
      onClick={onView} 
      variant="secondary"
      size={isMobile ? 'small' : 'medium'}
      sx={isMobile ? { fontSize: '12px', padding: '0.5rem 0.75rem' } : {}}
    >
      View My Designs
    </Button>
  </Box>
);

const RecentSavedDesignInner = () => {
  const { data, isLoading } = useRecentSavedDesign();
  const [slides, setSlides] = useState([]);
  const firedInteractions = useRef([]);
  const { trackInteraction } = useDesignTracking();
  const isMobile = useMobileBreakpoint();
  
  const {
    front_design_v2,
    back_design_v2,
    name,
    product_name,
    updated_at,
    edit_url
  } = data || {};

  useEffect(() => {
    if (front_design_v2) {
      setSlides([
        <img key="front" src={`${HOST}${front_design_v2}`} alt="Front" />,
        back_design_v2 && <img key="back" src={`${HOST}${back_design_v2}`} alt="Back" />
      ].filter(Boolean));
      
      const productFind = document.querySelector('.pc-Subcategories-section-product-card.product-find');
      if (productFind) {
        productFind.style.display = 'none';
      }
      // Add class to saved design card if design is present
      const savedDesignCard = document.querySelector('.pc-Subcategories-section-product-card.saved-design');
      if (savedDesignCard && !savedDesignCard.classList.contains('saved-design-present')) {
        savedDesignCard.classList.add('saved-design-present');
      }

    }
  }, [front_design_v2, back_design_v2]);

  useEffect(() => {
    if (!isLoading) {
      if (firedInteractions.current.includes(SAVE_INTERACTION_LABEL) || firedInteractions.current.includes(CUSTOM_INTERACTION_LABEL)) {
        return;
      }
      const label = data?.front_design_v2 ? SAVE_INTERACTION_LABEL : CUSTOM_INTERACTION_LABEL;
      trackInteraction(label);
      firedInteractions.current.push(label);
    }
  }, [isLoading, data, trackInteraction]);

  if (isLoading || !slides.length) return null;

  const handleEditDesign = () => {
    trackInteraction(EDIT_INTERACTION_LABEL);
    window.open(`${HOST}${edit_url}`, '_blank');
  };

  const handleViewDesigns = () => {
    trackInteraction(VIEW_INTERACTION_LABEL);
    window.open(`${HOST}/account/designs`, '_blank');
  };

  if (isMobile) {
    return (
      <Box sx={{ 
        maxWidth: '100%',
        margin: '-0.75rem',
        padding: '0.75rem'
      }}>
        <Typography
          sx={{ 
            color: '#242424',
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '0.75rem'
          }}
        >
          Your Latest Save
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          <Box sx={{ 
            flexBasis: '40%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '0.5rem',
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              boxShadow: 'rgb(245, 245, 245) 0px 0px 60px 24px inset',
              mixBlendMode: 'darken',
              zIndex: 1
            }
          }}>
            <Carousel
              sx={{
                aspectRatio: '222 / 260',
                '.keen-slider__slide': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  width: '100%',
                  height: '100%',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }
                }
              }}
              dots={false}
              sliderHeight={MOBILE_CAROUSEL_HEIGHT}
              slides={slides}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DesignDetails 
              name={name}
              product_name={product_name}
              updated_at={updated_at}
              isMobile={true}
            />
            <DesignActions 
              onEdit={handleEditDesign}
              onView={handleViewDesigns}
              isMobile={true}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 245}}>
      <Typography
        sx={{ 
          color: '#242424',
          fontSize: '24px',
          fontWeight: '700',
        }}
      >
        Your Latest Save
      </Typography>
      <Carousel
        sx={{
          marginTop: '0.75rem',
          '.keen-slider__slide': {
            backgroundColor: '#ffffff',
            borderRadius: '8px'
          }
        }}
        dots={false}
        sliderHeight={CAROUSEL_HEIGHT}
        slides={slides}
      />
      <DesignDetails 
        name={name}
        product_name={product_name}
        updated_at={updated_at}
        isMobile={false}
      />
      <DesignActions 
        onEdit={handleEditDesign}
        onView={handleViewDesigns}
        isMobile={false}
      />
    </Box>
  );
};

const RecentSavedDesign = () => {
  return (
    <PigmentThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RecentSavedDesignInner />
      </QueryClientProvider>
    </PigmentThemeProvider>
  );
};

export default RecentSavedDesign;
